import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { stylingApi } from '../lib/api.js';
import type { SkuAnalysisItem, UnderexposedSku } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { formatNumber } from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business SKU分析ページ。表示回数・選定率・position帯別分析・評価高×在庫余りSKU検出を表示
 * @graph-connects stylingApi [calls] Styling APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 */
export const Route = createFileRoute('/styling/sku-analysis')({
  component: SkuAnalysisPage,
});

type SortKey = 'displayCount' | 'selectionRate' | 'avgPosition';
type SortDir = 'asc' | 'desc';

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business SkuAnalysisPage定義
 * @graph-connects none
 */
function SkuAnalysisPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');
  const [sortKey, setSortKey] = useState<SortKey>('displayCount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const skuQuery = useQuery({
    queryKey: queryKeys.styling.skuAnalysis(startDate, endDate),
    queryFn: () => stylingApi.getSkuAnalysis(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const positionQuery = useQuery({
    queryKey: queryKeys.styling.positionAnalysis(startDate, endDate),
    queryFn: () => stylingApi.getPositionAnalysis(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const underexposedQuery = useQuery({
    queryKey: queryKeys.styling.underexposedSkus(startDate, endDate),
    queryFn: () => stylingApi.getUnderexposedSkus(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  const sortedSkus = useMemo(() => {
    if (!skuQuery.data) return [];
    return [...skuQuery.data.skus].sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [skuQuery.data, sortKey, sortDir]);

  const lowSelectionSkus = useMemo(() => {
    if (!skuQuery.data) return [];
    return [...skuQuery.data.skus]
      .filter((s) => s.displayCount > 50)
      .sort((a, b) => (a.selectionRate ?? 0) - (b.selectionRate ?? 0))
      .slice(0, 30);
  }, [skuQuery.data]);

  const positionChartData = useMemo(() => {
    if (!positionQuery.data) return [];
    return positionQuery.data.bands.map((b) => ({
      ...b,
      selectionRatePct: b.selectionRate != null ? Math.round(b.selectionRate * 10000) / 100 : 0,
    }));
  }, [positionQuery.data]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortLabel = (key: SortKey) => {
    const labels: Record<SortKey, string> = {
      displayCount: '表示回数',
      selectionRate: '選定率',
      avgPosition: '平均Position',
    };
    const arrow = sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';
    return labels[key] + arrow;
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('styling.skuAnalysis.title', 'SKU 分析')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-tertiary)]">
            {t('styling.skuAnalysis.description', 'SKU の表示・選定・評価の分析ダッシュボード')}
          </p>
        </div>
        <DateRangeSelector
          preset={preset}
          onPresetChange={setPreset}
          startDate={startDate}
          endDate={endDate}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* Position × 選定率チャート */}
      {positionChartData.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.skuAnalysis.positionChart', 'Position 帯別 選定率')}
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={positionChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="positionBand" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${String(v)}%`} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-text-primary)',
                  }}
                  formatter={((value: number) => [`${value.toFixed(2)}%`, '選定率']) as never}
                  labelFormatter={((label: string) => `Position ${label}`) as never}
                />
                <Bar
                  dataKey="selectionRatePct"
                  name={t('styling.skuAnalysis.selectionRate', '選定率')}
                  fill="var(--chart-1)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex gap-4 text-xs text-[var(--color-text-muted)]">
            {positionChartData.map((b) => (
              <span key={b.positionBand}>
                {b.positionBand}: {formatNumber(b.totalImpressions)} imp
              </span>
            ))}
          </div>
        </div>
      )}

      {/* TOP SKU タイルグリッド */}
      {sortedSkus.length > 0 && (
        <div className="card p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {t('styling.skuAnalysis.topSkus', 'TOP SKU（表示回数順）')}
            </h2>
            <div className="flex gap-1">
              {(['displayCount', 'selectionRate', 'avgPosition'] as SortKey[]).map((key) => (
                <button
                  key={key}
                  onClick={() => {
                    handleSort(key);
                  }}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    sortKey === key
                      ? 'bg-[var(--color-accent-primary)] text-white'
                      : 'bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-active)]'
                  }`}
                >
                  {sortLabel(key)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {sortedSkus.map((sku) => (
              <SkuTile key={sku.skuId} sku={sku} onImageClick={setLightboxUrl} />
            ))}
          </div>
        </div>
      )}

      {/* 表示されるが選ばれない SKU */}
      {lowSelectionSkus.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.skuAnalysis.lowSelection', '表示されるが選ばれない SKU（表示50回以上）')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {lowSelectionSkus.map((sku) => (
              <SkuTile key={sku.skuId} sku={sku} onImageClick={setLightboxUrl} />
            ))}
          </div>
        </div>
      )}

      {/* 評価高 × 在庫余り SKU */}
      {underexposedQuery.data && underexposedQuery.data.skus.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
            {t('styling.skuAnalysis.underexposed', '評価高 × 在庫余り × 選定率低 SKU')}
          </h2>
          <p className="text-xs text-[var(--color-text-muted)] mb-3">
            {t('styling.skuAnalysis.underexposedDesc', '評価 4.0+、在庫 10+、選定率 5%未満')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {underexposedQuery.data.skus.map((sku) => (
              <UnderexposedTile key={sku.skuId} sku={sku} onImageClick={setLightboxUrl} />
            ))}
          </div>
        </div>
      )}

      {/* ローディング・空状態 */}
      {(skuQuery.isLoading || positionQuery.isLoading) && (
        <div className="card p-12 text-center text-[var(--color-text-muted)]">
          {t('common.loading', '読み込み中...')}
        </div>
      )}
      {skuQuery.data && skuQuery.data.skus.length === 0 && (
        <div className="card p-12 text-center text-[var(--color-text-muted)]">
          {t('styling.skuAnalysis.noData', '対象期間のデータがありません')}
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70"
          onClick={() => {
            setLightboxUrl(null);
          }}
        >
          <img
            src={lightboxUrl}
            alt=""
            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business SkuTile定義
 * @graph-connects none
 */
function SkuTile({
  sku,
  onImageClick,
}: {
  sku: SkuAnalysisItem;
  onImageClick: (url: string) => void;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] overflow-hidden hover:shadow-md transition-shadow">
      {sku.imageUrl ? (
        <img
          src={sku.imageUrl}
          alt={`SKU ${String(sku.skuId)}`}
          className="w-full aspect-square object-cover cursor-pointer"
          loading="lazy"
          onClick={() => {
            if (sku.imageUrl) onImageClick(sku.imageUrl);
          }}
        />
      ) : (
        <div className="w-full aspect-square bg-[var(--color-bg-hover)] flex items-center justify-center text-[var(--color-text-muted)] text-xs">
          No Image
        </div>
      )}
      <div className="p-2 space-y-1">
        <p className="font-mono text-xs text-[var(--color-text-secondary)]">{sku.skuId}</p>
        <div className="flex justify-between text-xs">
          <span className="text-[var(--color-text-muted)]">表示</span>
          <span className="text-[var(--color-text-primary)] font-medium">
            {formatNumber(sku.displayCount)}
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-[var(--color-text-muted)]">選定率</span>
          <span className="text-[var(--color-text-primary)] font-medium">
            {sku.selectionRate != null ? `${(sku.selectionRate * 100).toFixed(1)}%` : '-'}
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-[var(--color-text-muted)]">Pos</span>
          <span className="text-[var(--color-text-primary)] font-medium">
            {sku.avgPosition != null ? sku.avgPosition.toFixed(0) : '-'}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business UnderexposedTile定義
 * @graph-connects none
 */
function UnderexposedTile({
  sku,
  onImageClick,
}: {
  sku: UnderexposedSku;
  onImageClick: (url: string) => void;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] overflow-hidden hover:shadow-md transition-shadow">
      {sku.imageUrl ? (
        <img
          src={sku.imageUrl}
          alt={`SKU ${String(sku.skuId)}`}
          className="w-full aspect-square object-cover cursor-pointer"
          loading="lazy"
          onClick={() => {
            if (sku.imageUrl) onImageClick(sku.imageUrl);
          }}
        />
      ) : (
        <div className="w-full aspect-square bg-[var(--color-bg-hover)] flex items-center justify-center text-[var(--color-text-muted)] text-xs">
          No Image
        </div>
      )}
      <div className="p-2 space-y-1">
        <p className="font-mono text-xs text-[var(--color-text-secondary)]">{sku.skuId}</p>
        <div className="flex justify-between text-xs">
          <span className="text-[var(--color-text-muted)]">評価</span>
          <span className="text-[var(--color-text-primary)] font-medium">
            {sku.weightedAvgRating?.toFixed(1) ?? '-'}
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-[var(--color-text-muted)]">在庫</span>
          <span className="text-[var(--color-text-primary)] font-medium">
            {sku.avgStock != null ? Math.round(sku.avgStock) : '-'}
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-[var(--color-text-muted)]">選定率</span>
          <span className="text-[var(--color-text-primary)] font-medium">
            {sku.selectionRate != null ? `${(sku.selectionRate * 100).toFixed(1)}%` : '-'}
          </span>
        </div>
      </div>
    </div>
  );
}
